import { useState, useCallback } from 'react'
import { api } from '../api/client.js'

export interface OrgDetailsResponse {
  id: string
  name?: string | null
  description?: string | null
  address?: string | null
  cityId?: number | null
  stateId?: number | null
  zip?: string | null
  countryId?: number | null
  timezoneId?: string | null
  phone?: string | null
  fax?: string | null
  website?: string | null
  contactPerson?: string | null
  email?: string | null
  contactPhone?: string | null
  domainId?: number | null
  nearingEscalationTime?: number | null
  conversationReplyWaitingTime?: number | null
  /** When true, agents are made Available automatically after login. */
  immediateAvailable?: boolean | null
  facebook?: string | null
  linkedin?: string | null
  instagram?: string | null
  pinterest?: string | null
  twitter?: string | null
  youtube?: string | null
  viber?: string | null
  properties?: string[] | null
  propertiesOrder?: string[] | null
  /** When true, AI auto-reply is enabled for inbound messages. */
  aiAutoReply?: boolean | null
  /** AG-05: tell customers when they are talking to AI (default true). */
  aiDisclosureEnabled?: boolean | null
  /** AG-05: disclosure wording; empty = built-in default. */
  aiDisclosureText?: string | null
  createdAt?: string
  updatedAt?: string
  createdBy?: number | null
  updatedBy?: number | null
}

export interface OrgDetailsInput {
  name?: string | null
  description?: string | null
  address?: string | null
  cityId?: number | null
  stateId?: number | null
  zip?: string | null
  countryId?: number | null
  timezoneId?: string | null
  phone?: string | null
  fax?: string | null
  website?: string | null
  contactPerson?: string | null
  email?: string | null
  contactPhone?: string | null
  domainId?: number | null
  nearingEscalationTime?: number | null
  conversationReplyWaitingTime?: number | null
  immediateAvailable?: boolean | null
  facebook?: string | null
  linkedin?: string | null
  instagram?: string | null
  pinterest?: string | null
  twitter?: string | null
  youtube?: string | null
  viber?: string | null
  properties?: string[] | null
  propertiesOrder?: string[] | null
  /** When true, AI auto-reply is enabled for inbound messages. */
  aiAutoReply?: boolean | null
  /** AG-05: tell customers when they are talking to AI (default true). */
  aiDisclosureEnabled?: boolean | null
  /** AG-05: disclosure wording; empty = built-in default. */
  aiDisclosureText?: string | null
}

export function useOrgDetails() {
  const [orgDetails, setOrgDetails] = useState<OrgDetailsResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchOrgDetails = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const response = await api.get<{ orgDetails: OrgDetailsResponse | null }>('/admin/org-details')
      setOrgDetails(response.orgDetails)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch org details')
    } finally {
      setLoading(false)
    }
  }, [])

  const create = useCallback(async (body: OrgDetailsInput): Promise<OrgDetailsResponse> => {
    setLoading(true)
    try {
      const response = await api.post<{ orgDetails: OrgDetailsResponse }>('/admin/org-details', body)
      setOrgDetails(response.orgDetails)
      return response.orgDetails
    } finally {
      setLoading(false)
    }
  }, [])

  const update = useCallback(async (body: OrgDetailsInput): Promise<OrgDetailsResponse> => {
    setLoading(true)
    try {
      const response = await api.patch<{ orgDetails: OrgDetailsResponse }>('/admin/org-details', body)
      setOrgDetails(response.orgDetails)
      return response.orgDetails
    } finally {
      setLoading(false)
    }
  }, [])

  return {
    orgDetails,
    loading,
    error,
    fetchOrgDetails,
    create,
    update,
  }
}
